// src/components/airports/Forms/FormAddEditAirport.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Box, Paper, Typography, Grid, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import i18next from "i18next";

import useCreateAirport from "../../../hooks/airports/useCreateAirport";
import useUpdateAirport from "../../../hooks/airports/useUpdateAirport";
import useGetAirport from "../../../hooks/airports/useGetAirport";
import useGetCountriesForSelect from "../../../hooks/countries/useGetCountriesForSelect";
import useGetCitiesForSelect from "../../../hooks/cities/useGetCitiesForSelect";
import { transformOptionsToIdName } from "../../../utils/forms/transformHelper";

import FormInput from "../../inputs/FormInput";
import LanguageToggle from "../../global/LanguageToggle";

const FormAddEditAirport = ({ isEdit = false, onSubmitSuccess }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    // Hooks
    const { createAirport, isPending: isPendingCreate } = useCreateAirport();
    const { updateAirport, isPending: isPendingUpdate } = useUpdateAirport();
    const { airport, isLoading: isLoadingAirport } = useGetAirport(isEdit ? id : null);

    const { options: countriesOptions } = useGetCountriesForSelect();
    const [countryId, setCountryId] = useState("");
    const [cityId, setCityId] = useState("");

    const { options: citiesOptions } = useGetCitiesForSelect(countryId);

    // Form States
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [terminalAr, setTerminalAr] = useState("");
    const [terminalEn, setTerminalEn] = useState("");
    const [code, setCode] = useState("");
    const [status, setStatus] = useState("active");
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isEdit && airport) {
            setNameAr(airport?.name?.ar || "");
            setNameEn(airport?.name?.en || "");
            setTerminalAr(airport?.terminal?.ar || "");
            setTerminalEn(airport?.terminal?.en || "");
            setCountryId(airport?.country_id || "");
            setCityId(airport?.city_id || "");
            setCode(airport?.code || "");
            setStatus(airport?.status || "active");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, airport]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        const payload = {
            country_id: countryId,
            city_id: cityId,
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            terminal: {
                ar: terminalAr,
                en: terminalEn,
            },
            code,
            status,
        };

        if (isEdit) {
            updateAirport(
                { id, data: payload },
                {
                    onSuccess: () => {
                        if (onSubmitSuccess) onSubmitSuccess();
                        navigate("/airports");
                    },
                    onError: (err) => setErrors(err?.response?.data?.errors || {}),
                }
            );
        } else {
            createAirport(payload, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                    navigate("/airports");
                },
                onError: (err) => setErrors(err?.response?.data?.errors || {}),
            });
        }
    };

    const cOptions = useMemo(() => transformOptionsToIdName(countriesOptions), [countriesOptions]);
    const ciOptions = useMemo(() => transformOptionsToIdName(citiesOptions), [citiesOptions]);

    if (isEdit && isLoadingAirport) return <Typography color="primary" sx={{ p: 4 }}>Loading...</Typography>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {isEdit ? (isArabic ? "تعديل مطار" : "Edit Airport") : (isArabic ? "إضافة مطار جديد" : "Add New Airport")}
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Location Info */}
                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="select"
                                label={isArabic ? "الدولة" : "Country"}
                                value={countryId}
                                options={cOptions}
                                onChange={(e) => {
                                    setCountryId(e.target.value);
                                    setCityId(""); // Cascading reset
                                }}
                                error={errors.country_id}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="select"
                                label={isArabic ? "المدينة" : "City"}
                                value={cityId}
                                options={ciOptions}
                                onChange={(e) => setCityId(e.target.value)}
                                error={errors.city_id}
                                required
                                disabled={!countryId}
                            />
                        </Grid>

                        {/* Name Info */}
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="أدخل اسم المطار بالعربي"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    error={errors["name.ar"] || errors.name_ar}
                                    required
                                />
                            </Grid>
                        )}
                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="AirPort Name"
                                    placeholder="Enter airport name in English"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    error={errors["name.en"] || errors.name_en}
                                    required
                                />
                            </Grid>
                        )}

                        {/* Terminal Info */}
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="التيرمينال / الجيت (بوابة)"
                                    placeholder="أدخل رقم أو اسم التيرمينال / الجيت بالعربي"
                                    value={terminalAr}
                                    onChange={(e) => setTerminalAr(e.target.value)}
                                    error={errors["terminal.ar"] || errors.terminal_ar}
                                />
                            </Grid>
                        )}
                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Terminal / Gate"
                                    placeholder="Enter terminal or gate name in English"
                                    value={terminalEn}
                                    onChange={(e) => setTerminalEn(e.target.value)}
                                    error={errors["terminal.en"] || errors.terminal_en}
                                    required={false}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "كود المطار" : "Airport Code"}
                                placeholder={isArabic ? "مثلاً: JFK" : "e.g., JFK"}
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                error={errors.code}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="select"
                                label={isArabic ? "الحالة" : "Status"}
                                value={status}
                                options={[
                                    { id: "active", name: isArabic ? "نشط" : "Active" },
                                    { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
                                ]}
                                onChange={(e) => setStatus(e.target.value)}
                                error={errors.status}
                                required
                            />
                        </Grid>
                    </Grid>

                    {/* Actions */}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/airports")}
                            sx={{ minWidth: 120, borderRadius: 2 }}
                        >
                            {isArabic ? "إلغاء" : "Cancel"}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isPendingCreate || isPendingUpdate}
                            sx={{ minWidth: 150, borderRadius: 2 }}
                        >
                            {(isPendingCreate || isPendingUpdate) ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                isEdit ? (isArabic ? "تعديل" : "Update") : (isArabic ? "إضافة" : "Add")
                            )}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default FormAddEditAirport;
